import { useState, useEffect } from "react";
import { Users, Users2, Scale, Building2 } from "lucide-react";
import { TabCompletionHeader } from "./common/TabCompletionHeader";
import { useWizard } from "@/contexts/WizardContext";
import { MetricCard } from "./common/MetricCard";
import { EmployeeSupportCard } from "./social/EmployeeSupportCard";
import { DiversityInclusionCard } from "./social/DiversityInclusionCard";
import { HumanRightsCard } from "./social/HumanRightsCard";
import { CommunityContributionCard } from "./social/CommunityContributionCard";
import { WizardSectionNav } from "@/components/wizard/WizardSectionNav";
import { getSectionByCardKey } from "@shared/section-definitions";

export function SocialTab() {
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const {
    workforceCompletion,
    diversityInclusionCompletion,
    humanRightsCompletion,
    communityContributionCompletion,
    socialOverallCompletion,
    currentSectionId,
    currentSection,
    goToSection,
    isLocked,
  } = useWizard();

  const socialStatusLabel = isLocked ? "Section finalized" : "Section in progress";

  // Sync expandedMetric with currentSection
  useEffect(() => {
    if (currentSection && currentSection.tab === "social") {
      setExpandedMetric(currentSection.cardKey);
    }
  }, [currentSection]);

  const handleSectionExpand = (cardKey: string) => {
    setExpandedMetric(cardKey);
    const section = getSectionByCardKey("social", cardKey);
    if (section) {
      goToSection(section.id);
    }
  };

  const handleSectionClose = () => {
    setExpandedMetric(null);
  };

  const sectionCompletions = {
    employeeSupport: workforceCompletion,
    diversityInclusion: diversityInclusionCompletion,
    humanRights: humanRightsCompletion,
    communityContribution: communityContributionCompletion,
  };

  return (
    <div className="flex gap-6">
      {/* Left Sidebar Navigation */}
      <div className="w-64 flex-shrink-0 hidden lg:block sticky top-[8.25rem] self-start h-[calc(100vh-16.5rem)]">
        <WizardSectionNav
          currentTab="social"
          currentSectionId={currentSectionId}
          onSectionClick={(section) => handleSectionExpand(section.cardKey)}
          sectionCompletions={sectionCompletions}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-4">
        <div>
          <TabCompletionHeader
            title="Social"
            completion={socialOverallCompletion}
            statusLabel={socialStatusLabel}
          />
        </div>

        {expandedMetric === "employeeSupport" ? (
          <EmployeeSupportCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "diversityInclusion" ? (
          <DiversityInclusionCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "humanRights" ? (
          <HumanRightsCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "communityContribution" ? (
          <CommunityContributionCard onCollapse={handleSectionClose} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetricCard
              icon={Users}
              title="Employee Support & Wellbeing"
              description="Workforce policies, health & safety, working conditions, and employee wellbeing"
              completion={workforceCompletion}
              onClick={() => handleSectionExpand("employeeSupport")}
            />
            <MetricCard
              icon={Users2}
              title="Diversity & Inclusion"
              description="Diversity metrics, inclusion policies, and equal opportunity practices"
              completion={diversityInclusionCompletion}
              onClick={() => handleSectionExpand("diversityInclusion")}
            />
            <MetricCard
              icon={Scale}
              title="Human Rights"
              description="Supply chain due diligence, labor standards, and human rights commitments"
              completion={humanRightsCompletion}
              onClick={() => handleSectionExpand("humanRights")}
            />
            <MetricCard
              icon={Building2}
              title="Community Contribution"
              description="Social investments, partnerships, and local community engagement"
              completion={communityContributionCompletion}
              onClick={() => handleSectionExpand("communityContribution")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
